import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Table, type Column } from "@freightsimple/ui-core/Table/Table";
import { TextCell } from "@freightsimple/ui-core/Cells/TextCell";
import { type PerformanceServiceQuality } from "@freightsimple/generated-carriers-openapi-client/models/PerformanceServiceQuality";
import { type PerformanceServiceQualitySplit } from "@freightsimple/generated-carriers-openapi-client/models/PerformanceServiceQualitySplit";
import { formatPercent } from "@/App/Views/carrierFormatting";
import { PerformanceMetric } from "@/App/Performance/PerformanceMetric";

/**
 * Transit performance measured against the window the carrier themselves quoted.
 *
 * That framing is deliberate and is what makes the numbers hard to argue with: it
 * is their own promise against their own delivery, with no comparison to any
 * other carrier anywhere in it. A slow carrier who quotes honestly scores well
 * here, and a fast one who over-promises scores badly.
 */
export function ServiceQualityPanel({
  serviceQuality,
}: {
  serviceQuality: PerformanceServiceQuality;
}) {
  const spread = serviceQuality.daysBeyondEarliest;

  return (
    <Stack gap={4}>
      <Panel
        title="Against Your Own Quoted Window"
        description="What was quoted on the shipment against when it actually arrived. The earliest day of the window is your transit time; the latest is our estimate."
      >
        <Grid columns={3} gap="md">
          <PerformanceMetric
            label="Outside Transit Window"
            value={formatRate_(serviceQuality.beyondWindowRate)}
            hint="after the latest date on the quote"
            explanation="Of your delivered shipments, the share that arrived after the latest delivery date on the quote. That date is FreightSimple's estimate, not a transit time you gave us: we treat your transit time as the earliest possible day and predict a later one, aiming for most shipments to arrive inside the window. Arriving on that date counts as on time. Counted in business days from pickup. Shipments still moving, and any we have no delivery date for, are excluded from both sides."
          />
          <PerformanceMetric
            label="Days Beyond Earliest"
            value={formatDays_(spread.p50)}
            hint={`median of ${spread.shipments.toLocaleString("en-CA")} delivered shipments`}
            explanation="The median number of business days past the earliest day of your quoted window that your shipments actually arrived. Zero means you typically hit the earliest day; negative means you beat it. Measured against the earliest day, not the latest, so it shows how much of your own window you consume."
          />
          <PerformanceMetric
            label="Worst 10%"
            value={formatDays_(spread.p90)}
            hint="the tail a customer remembers"
            explanation="The 90th percentile of days past your earliest quoted day: one shipment in ten was at least this far past it. Shown because a customer's impression is set by the worst deliveries, not the typical one."
          />
        </Grid>
        {/* Both numbers are shown together on purpose: a carrier can drive the
            late rate to zero simply by quoting a wider window, and the
            days-past-earliest figure is what shows that. */}
        <T size="sm" color="muted">
          The window has two ends and they come from different places. The
          earliest day is your own transit time. The latest is our estimate of
          when the freight will realistically arrive, so &ldquo;Outside Transit
          Window&rdquo; is measured against our number, not yours. Days Beyond
          Earliest is measured against yours, which is why both are shown.
        </T>
      </Panel>

      <Panel
        title="How Much of Your Window You Use"
        description="Business days past the earliest day you quoted. Zero means you hit it; negative means you beat it."
      >
        <Grid columns={4} gap="md">
          <PerformanceMetric
            label="Average"
            value={formatDays_(spread.average)}
            size="xl"
            explanation="The mean number of business days past the earliest day of your quoted window, across every delivered shipment. Sensitive to a few very late shipments in a way the median is not — compare the two to see whether a high figure is typical or a tail."
          />
          <PerformanceMetric
            label="Median"
            value={formatDays_(spread.p50)}
            hint="p50"
            size="xl"
            explanation="The middle shipment: half arrived within this many business days of your earliest quoted day, half took longer. The typical result, unaffected by a handful of very late shipments."
          />
          <PerformanceMetric
            label="p90"
            value={formatDays_(spread.p90)}
            size="xl"
            explanation="One shipment in ten was at least this many business days past your earliest quoted day."
          />
          <PerformanceMetric
            label="p99"
            value={formatDays_(spread.p99)}
            size="xl"
            explanation="One shipment in a hundred was at least this many business days past your earliest quoted day. The far tail — on a small number of shipments this is effectively the single worst one."
          />
        </Grid>
      </Panel>

      <SplitTable_
        title="By Service"
        description="Late rates split by the service you quoted."
        header="Service"
        rows={serviceQuality.byService}
      />
      <SplitTable_
        title="By Delivery Location"
        description="Residential and limited-access deliveries often behave differently from commercial docks."
        header="Location Type"
        rows={serviceQuality.byDeliveryLocationType}
      />
    </Stack>
  );
}

function SplitTable_({
  title,
  description,
  header,
  rows,
}: {
  title: string;
  description: string;
  header: string;
  rows: PerformanceServiceQualitySplit[];
}) {
  const columns: Column<PerformanceServiceQualitySplit>[] = [
    {
      key: "label",
      header,
      render: (row) => <TextCell value={row.label} />,
    },
    {
      key: "shipments",
      header: "Shipments",
      align: "right",
      render: (row) => (
        <TextCell value={row.shipments.toLocaleString("en-CA")} />
      ),
    },
    {
      key: "late",
      header: "Delivered Late",
      align: "right",
      render: (row) => <TextCell value={formatRate_(row.beyondWindowRate)} />,
    },
    {
      key: "beyond",
      header: "Avg Days Past Earliest",
      align: "right",
      render: (row) => (
        <TextCell value={formatDays_(row.averageDaysBeyondEarliest)} />
      ),
    },
  ];

  return (
    <Panel title={title} description={description} fullBleed>
      <Table
        columns={columns}
        data={rows}
        rowKey={(row) => row.label}
        emptyMessage="Not enough delivered shipments in this period to break down."
      />
    </Panel>
  );
}

// An absent value means an empty denominator, not zero. "—" says we have nothing
// to go on; "0%" would claim a flawless record we can't support.
function formatRate_(value: number | undefined): string {
  return value === undefined ? "—" : formatPercent(value);
}

function formatDays_(value: number | undefined): string {
  return value === undefined ? "—" : `${value.toFixed(1)} days`;
}
